import React from 'react';

import { showModal } from 'modal.react.js';
import { Controller, useFormContext } from 'react-hook-form';

import Preview from 'components/Pages/Admin/shared/Preview';
import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import { ISelectOption, TSelectChangeHandler } from 'components/shared/BaseFormComponents/Select/types';
import Divider from 'components/shared/Divider';
import { FormAccordion, FormAccordionItem } from 'components/shared/FormAccordion';
import area from 'components/shared/GlobalModalArea/area';
import Modal from 'components/shared/GlobalModalArea/Modal';
import { HookCheckbox, HookInput } from 'components/shared/HookFormComponents';
import { HookWysiwyg } from 'components/shared/HookFormComponents/Wysiwyg';
import Visibility from 'components/shared/Icon/icons/Visibility';
import Text from 'components/shared/Typography/Text';
import { EOrganizedBy, IApiFlightTripEvent } from 'detailsv2/structure';
import { createOptionFromPageV2 } from 'utils/createOptionFromPage';

import { infoLoadTypes } from '../../../../../constants';
import { getOptionsLoader } from '../../functions';
import cs from '../../index.module.css';
import { ChangePagePopover } from '../../shared';
import IconPicker from '../../shared/IconPicker';
import { scrollToBottomById } from '../functions';
import { FlightAPIOptions } from './FlightAPIOption/types';
import { FlightTabs } from './FlightTabs';
import s from './index.module.css';
import { FlightTab } from './types';

type Props = {
  tripEvent: IApiFlightTripEvent;
  activeItems: FlightAPIOptions;
  setActiveItems: React.Dispatch<React.SetStateAction<FlightAPIOptions>>;
  activeTab: FlightTab;
  setActiveTab: (tab: FlightTab) => void;
  isDisabled: boolean;
  organizedBy: EOrganizedBy;
  popover: {
    ref: React.RefObject<HTMLDivElement>;
    isOpen: boolean;
    onClose: () => void;
    onKeep: () => void;
    onReset: () => void;
  };
  onChangePage: TSelectChangeHandler;
};

export const FlightForm = (props: Props) => {
  const methods = useFormContext();
  const { control } = methods;

  const loadOptions = getOptionsLoader(infoLoadTypes, createOptionFromPageV2);
  const openPagePreview = (pageId: number) => {
    showModal(area, (res) => (
      <Modal noPadding size="lg" isOpen toClose={res} stopMouseDownPropagation>
        <Preview pageId={pageId} />
      </Modal>
    ));
  };

  return (
    <FormAccordion>
      <FormAccordionItem header="Required Information" isRequired>
        <FlightTabs {...props} />

        <Divider />

        <div className={cs.row}>
          <div>
            <Text isStrong size="regular">
              Icons
            </Text>
            <Text color="gray-600" size="medium">
              Optionally customize your icon,
              <br /> because we’re awesome.
            </Text>
          </div>
          <div>
            <IconPicker
              name="icon"
              control={control}
              eventType={props.tripEvent.type}
              isImprovedUIOnEventModalsFeature
            />
          </div>
        </div>
      </FormAccordionItem>

      {props.activeTab === FlightTab.MANUAL && (
        <FormAccordionItem header="Flight Details">
          <div className={s.container}>
            <HookInput name="data.bookedBy" control={control} label="Booked By" />
            <HookInput name="data.confirmationNumber" control={control} label="Confirmation Number" />
            <HookInput name="data.airline" control={control} label="Airline" />
            <HookInput name="data.flightNumber" control={control} label="Flight Number" />
            <HookInput name="data.duration" control={control} label="Duration" />
          </div>
        </FormAccordionItem>
      )}

      <FormAccordionItem header="Notes">
        <HookWysiwyg
          name="data.notes"
          control={control}
          placeholder="Flight confirmation number, seat assignments, etc..."
        />
      </FormAccordionItem>

      <FormAccordionItem header="Event Title">
        <Text color="gray-500" size="medium" className={s.title}>
          Leave this blank to show the default text or add a custom title, e.g. Maya’s flight to Rome
        </Text>
        <HookInput name="data.displaySettings" control={control} label="New Title" />
      </FormAccordionItem>

      <FormAccordionItem header="Add Information From Content Library">
        <Text color="gray-500" size="medium" className={s.title}>
          This information will appear within the detailed daily itinerary. e.g., baggage restrictions or plane
          descriptions.
        </Text>

        <div>
          <Controller
            name="attachedPage"
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <div ref={props.popover.ref}>
                  <BaseSelect
                    placeholder="Search Content Library"
                    isClearable
                    //@ts-ignore
                    optionActionIcon={Visibility}
                    onOptionActionClick={openPagePreview}
                    onMenuOpen={() => scrollToBottomById('AModalBody')}
                    defaultOption={value as ISelectOption}
                    loadOptions={loadOptions}
                    onChange={(name, id) => {
                      if (id === null && name === null) {
                        onChange(undefined);
                      } else {
                        onChange({ id, name });
                      }

                      props.onChangePage(name, id);
                    }}
                    errorMessage={error?.message}
                  />
                </div>
              );
            }}
          />

          {props.popover.isOpen && (
            <ChangePagePopover
              onClose={props.popover.onClose}
              onKeep={props.popover.onKeep}
              onReset={props.popover.onReset}
              anchorEl={props.popover.ref.current}
            />
          )}
        </div>

        <div className={cs.collapseCheckbox}>
          <HookCheckbox name="data.isCollapsible" control={control} className={cs.checkboxText}>
            Collapse content within More Details
          </HookCheckbox>
        </div>
      </FormAccordionItem>

      <div className={cs.marginBottom} />
    </FormAccordion>
  );
};
